import { useEffect } from "react";
import { useDyteMeeting } from "@dytesdk/react-web-core";
import { Mic, MicOff, PhoneOff, Video, VideoOff } from "lucide-react";

import { Button } from "@/components/ui/button";

interface VideoControlsProps {
  isMicOn: boolean;
  setIsMicOn: (on: boolean) => void;
  isVideoOn: boolean;
  setIsVideoOn: (on: boolean) => void;
  showParticipants: boolean;
  setShowParticipants: (show: boolean) => void;
  handleLeaveCall: () => void;
}

export default function VideoControls({
  isMicOn,
  setIsMicOn,
  isVideoOn,
  setIsVideoOn,
  showParticipants,
  setShowParticipants,
  handleLeaveCall,
}: VideoControlsProps) {
  const { meeting } = useDyteMeeting();

  useEffect(() => {
    if (!meeting) return;
    const handleCameraUpdate = ({
      videoEnabled,
    }: {
      videoEnabled: boolean;
    }) => {
      setIsVideoOn(videoEnabled);
    };
    meeting.self.on("videoUpdate", handleCameraUpdate);
    return () => {
      meeting.self.removeListener("videoUpdate", handleCameraUpdate);
    };
  }, [meeting]);

  return (
    <div className="absolute inset-x-0 bottom-0 z-20 bg-black/50 p-6 backdrop-blur-sm">
      <div className="flex items-center justify-center gap-4">
        {/* Microphone Control */}
        <div className="flex flex-col items-center gap-2">
          <Button
            size="lg"
            variant={isMicOn ? "default" : "destructive"}
            onClick={async () => {
              if (!meeting) return;
              if (isMicOn) {
                await meeting.self.disableAudio();
                setIsMicOn(false);
              } else {
                await meeting.self.enableAudio();
                setIsMicOn(true);
              }
            }}
            className={`size-14 rounded-full p-0 ${
              isMicOn
                ? "bg-white/20 text-white hover:bg-white/30"
                : "bg-red-100 text-red-800 hover:bg-white/30"
            }`}
          >
            {isMicOn ? (
              <Mic className="size-6" />
            ) : (
              <MicOff className="size-6" />
            )}
          </Button>

          <span className="w-14 text-center text-xs text-white/70">
            {isMicOn ? "Mute" : "Unmute"}
          </span>
        </div>

        {/* Video Control */}
        <div className="flex flex-col items-center gap-2">
          <Button
            size="lg"
            variant={isVideoOn ? "secondary" : "destructive"}
            onClick={async () => {
              if (!meeting) return;
              if (isVideoOn) {
                await meeting.self.disableVideo();
                setIsVideoOn(false);
              } else {
                await meeting.self.enableVideo();
                setIsVideoOn(true);
              }
            }}
            className={`size-14 rounded-full p-0 ${
              isVideoOn
                ? "bg-white/20 text-white hover:bg-white/30"
                : "bg-red-100 text-red-800 hover:bg-white/30"
            }`}
          >
            {isVideoOn ? (
              <Video className="size-6" />
            ) : (
              <VideoOff className="size-6" />
            )}
          </Button>

          <span className="w-20 text-center text-xs text-white/70">
            {isVideoOn ? "Stop Video" : "Start Video"}
          </span>
        </div>

        {/* Participants Toggle */}
        {/* <Button
          size="lg"
          variant="secondary"
          onClick={() => setShowParticipants(!showParticipants)}
          className={`size-14 rounded-full p-0 ${
            showParticipants
              ? "bg-primary text-white hover:bg-primary/90"
              : "bg-white/20 text-white hover:bg-white/30"
          }`}
        >
          <Users className="size-6" />
        </Button> */}

        {/* Leave Call */}
        <div className="flex flex-col items-center gap-2">
          <Button
            size="lg"
            variant="destructive"
            onClick={handleLeaveCall}
            className="h-14 w-24 rounded-lg bg-red-600 p-0 hover:bg-red-700"
          >
            <PhoneOff className="size-6" />
          </Button>
          <span className="w-20 text-center text-xs text-white/70">Leave</span>
        </div>
      </div>
    </div>
  );
}
